class PredictiveSearch extends SearchForm {
  constructor() {
    super();
    this.cachedResults = {};
    this.predictiveSearchResults = this.closest(".details").querySelector('[data-predictive-search]');
    this.allPredictiveSearchInstances =
      document.querySelectorAll('predictive-search');
    this.isOpen = false;
    this.abortController = new AbortController();
    this.searchTerm = '';
    this.setupEventListeners();
    this.handleEnterRedirect();
  }

  setupEventListeners() {
    // Some predictive-search elements (eg. the results-only container in the
    // search modal) don't wrap an <input>, so there is nothing to wire up here.
    if (!this.input) return;

    if (this.input.form) {
      this.input.form.addEventListener('submit', this.onFormSubmit.bind(this));
    }
    this.input.addEventListener('focus', this.onFocus.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    // Results render in the search modal, outside this element: tabbing into them
    // must not close them, and tabbing out of them should.
    this.resultsModal = this.closest('.details')?.querySelector('.search-modal');
    this.resultsModal?.addEventListener('focusout', this.onFocusOut.bind(this));
    this.addEventListener('keyup', this.onKeyup.bind(this));
    this.addEventListener('keydown', this.onKeydown.bind(this));

    const searchButton = this.input.form?.querySelector('.search__button');
    if (searchButton) {
      searchButton.addEventListener('click', (e) => {
        const term = this.getQuery();
        if (term.length > 0) {
          e.preventDefault();
          window.location.href = `/search?type=product&options[prefix]=last&options[unavailable_products]=show&q=${encodeURIComponent(this.withWildcard(term))}`;
        }
      });
    }
  }

  getQuery() {
    return this.normalizeQuery(this.input.value.trim());
  }

  onChange() {
    super.onChange();
    const newSearchTerm = this.getQuery();
    if (!this.searchTerm || !newSearchTerm.startsWith(this.searchTerm)) {
      // Remove the results when they are no longer relevant for the new search term
      // so they don't show up when the dropdown opens again
      this.querySelector("#predictive-search-results-groups-wrapper")?.remove();
    }

    // Update the term asap, don't wait for the predictive search query to finish loading
    this.updateSearchForTerm(this.searchTerm, newSearchTerm);

    this.searchTerm = newSearchTerm;

    if (!this.searchTerm.length) {
      this.close(true);
      return;
    }

    this.getSearchResults(this.searchTerm);
  }

  onFormSubmit(event) {
    if (!this.getQuery().length || this.querySelector('[aria-selected="true"] a')) {
      event.preventDefault();
      return;
    }

    // Native submits (eg. mobile keyboards whose "search" key doesn't emit a
    // keydown we can intercept) post the raw input value, so normalize it here
    // too - otherwise "OR 1" reaches /search as a boolean query.
    this.input.value = this.getQuery();
  }

  handleEnterRedirect() {
    if (!this.input) return;

    this.input.addEventListener('keydown', (e) => {
      if (e.code === "Enter") {
        e.preventDefault(); // stop predictive dropdown
        const term = this.getQuery();

        if (term.length > 0) {
          // 🔥 Force correct Shopify search URL
          window.location.href = `/search?type=product&q=${encodeURIComponent(this.withWildcard(term))}&options[prefix]=last&options[unavailable_products]=show`;
        }
      }
    }, { capture: true });
  }

  onFormReset(event) {
    super.onFormReset(event);
    if (super.shouldResetForm()) {
      this.searchTerm = '';
      this.abortController.abort();
      this.abortController = new AbortController();
      this.closeResults(true);
    }
  }

  onFocus() {
    const currentSearchTerm = this.getQuery();

    if (!currentSearchTerm.length) return;

    if (this.searchTerm !== currentSearchTerm) {
      // Search term was changed from other search input, treat it as a user change
      this.onChange();
    } else if (this.getAttribute('results') === 'true') {
      this.open();
    } else {
      this.getSearchResults(this.searchTerm);
    }
  }

  onFocusOut() {
    setTimeout(() => {
      const active = document.activeElement;
      if (this.contains(active) || this.resultsModal?.contains(active)) return;
      this.close();
    })
  }

  onKeyup(event) {   
    if (!this.getQuery().length) this.close(true);
    event.preventDefault();    
    switch (event.code) {
      case 'ArrowUp':
        this.switchOption('up')
        break;
      case 'ArrowDown':
        this.switchOption('down');
        break;
      case 'Enter':
        this.selectOption();
        break;
    }
  }


  onKeydown(event) {
    // Prevent the cursor from moving in the input when using the up and down arrow keys
    if (
      event.code === 'ArrowUp' ||
      event.code === 'ArrowDown'
    ) {
      event.preventDefault();
    }
  }



  updateSearchForTerm(previousTerm, newTerm) {
    const searchForTextElement = this.querySelector(
      "[data-predictive-search-search-for-text]"
    );
    const currentButtonText = searchForTextElement?.innerText;
    if (currentButtonText) {
      if (currentButtonText.match(new RegExp(previousTerm, "g")).length > 1) {
        // The new term matches part of the button text and not just the search term, do not replace to avoid mistakes
        return;
      }
      const newButtonText = currentButtonText.replace(previousTerm, newTerm);
      searchForTextElement.innerText = newButtonText;
    }
  }

  switchOption(direction) {
    if (!this.getAttribute('open')) return;

    const moveUp = direction === 'up';
    const selectedElement = this.querySelector('[aria-selected="true"]');

    // Filter out hidden elements (duplicated page and article resources) thanks
    // to this https://developer.mozilla.org/en-US/docs/Web/API/HTMLElement/offsetParent
    const allVisibleElements = Array.from(
      this.querySelectorAll("li, button.predictive-search__item")
    ).filter((element) => element.offsetParent !== null);
    let activeElementIndex = 0;

    if (moveUp && !selectedElement) return;

    let selectedElementIndex = -1;
    let i = 0;

    while (selectedElementIndex === -1 && i <= allVisibleElements.length) {
      if (allVisibleElements[i] === selectedElement) {
        selectedElementIndex = i;
      }
      i++;
    }

    this.statusElement.textContent = "";

    if (!moveUp && selectedElement) {
      activeElementIndex =
        selectedElementIndex === allVisibleElements.length - 1
          ? 0
          : selectedElementIndex + 1;
    } else if (moveUp) {
      activeElementIndex =
        selectedElementIndex === 0
          ? allVisibleElements.length - 1
          : selectedElementIndex - 1;
    }

    if (activeElementIndex === selectedElementIndex) return;

    const activeElement = allVisibleElements[activeElementIndex];

    activeElement.setAttribute('aria-selected', true);
    if (selectedElement) selectedElement.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', activeElement.id);
  }

  selectOption() {
    const selectedOption = this.querySelector(
      '[aria-selected="true"] a, button[aria-selected="true"]'
    );

    if (selectedOption) selectedOption.click();
  }

  supportPredictiveApi() {
    const shopifyFeatureRequests = JSON.parse(document.getElementById("shopify-features").innerHTML);
    return shopifyFeatureRequests["predictiveSearch"];
  }

  getSearchResults(searchTerm) {
    const queryKey = searchTerm.replace(" ", "-").toLowerCase();
    this.setLiveRegionLoadingState();

    if (this.cachedResults[queryKey]) {
      this.renderSearchResults(this.cachedResults[queryKey]);
      return;
    }

    if(this.supportPredictiveApi()) {
      fetch(
        `${routes.predictive_search_url}?q=${encodeURIComponent(
          searchTerm
        )}&section_id=predictive-search&resources[type]=product,collection,page,article,query&resources[limit]=10&resources[options][unavailable_products]=show`,
        { signal: this.abortController.signal }
      )
        .then((response) => {
          if (!response.ok) {
            var error = new Error(response.status);
            this.close();
            throw error;
          }

          return response.text();
        })
        .then((text) => {
          const sectionEl = new DOMParser().parseFromString(text, 'text/html').querySelector('#shopify-section-predictive-search');
          const hasProducts = !!sectionEl.querySelector('#predictive-search-results-groups-wrapper');

          // The native Predictive Search API doesn't support options[prefix], so a
          // partial word (eg. "yuv") won't match a product title (eg. "Yuval 2") the
          // way the regular Storefront Search does. When it comes back with nothing,
          // fall back to a prefix-matching search so partial-word queries still work.
          if (!hasProducts && searchTerm.length) {
            return this.fetchPrefixFallbackProducts(searchTerm).then((fallbackGroup) => {
              if (fallbackGroup) {
                const resultsContainer = sectionEl.querySelector('#predictive-search-results');
                resultsContainer.insertAdjacentElement('afterbegin', fallbackGroup.wrapper);

                const liveRegion = sectionEl.querySelector('[data-predictive-search-live-region-count-value]');
                if (liveRegion && fallbackGroup.liveRegionText) liveRegion.textContent = fallbackGroup.liveRegionText;
              }

              return sectionEl.innerHTML;
            });
          }

          return sectionEl.innerHTML;
        })
        .then((resultsMarkup) => {
          // Save bandwidth keeping the cache in all instances synced
          this.allPredictiveSearchInstances.forEach(
            (predictiveSearchInstance) => {
              predictiveSearchInstance.cachedResults[queryKey] = resultsMarkup;
            }
          );
          this.renderSearchResults(resultsMarkup);
        })
        .catch((error) => {
          if (error?.code === 20) {
            // Code 20 means the call was aborted
            return;
          }
          this.close();
          throw error;
        });
    } else {
      fetch(
        `${window.Shopify.routes.root}search?q=${encodeURIComponent(
          this.withWildcard(searchTerm)
        )}&section_id=predictive-search-compatibility&type=product&options[unavailable_products]=show&options[prefix]=last`,
        { signal: this.abortController.signal }
      )
        .then((response) => {
          if (!response.ok) {
            var error = new Error(response.status);
            this.close();
            throw error;
          }

          return response.text();
        })
        .then((text) => {
          const resultsMarkup = new DOMParser().parseFromString(text, 'text/html').querySelector('#shopify-section-predictive-search-compatibility').innerHTML;
          // Save bandwidth keeping the cache in all instances synced
          this.allPredictiveSearchInstances.forEach(
            (predictiveSearchInstance) => {
              predictiveSearchInstance.cachedResults[queryKey] = resultsMarkup;
            }
          );
          this.renderSearchResults(resultsMarkup);
        })
        .catch((error) => {
          if (error?.code === 20) {
            // Code 20 means the call was aborted
            return;
          }
          this.close();
          throw error;
        });
    }
  }

  // Shopify's options[prefix]=last does NOT reliably prefix-match on this store
  // (verified: q=yuv finds nothing, q=yuv* finds "Yuval 2") - a trailing wildcard
  // on the last word is what actually triggers partial-word matching here.
  withWildcard(term) {
    const trimmed = term.trim();
    return trimmed.endsWith('*') ? trimmed : `${trimmed}*`;
  }

  // Shopify reads a standalone uppercase AND / OR / NOT as a boolean operator
  // (verified: q=NOT coffee excludes coffee, q=not coffee finds it). That makes
  // "OR 1" parse as (operator OR) + "1" and return every product with a "1" in
  // the title, while "or 1" is matched literally. Shoppers type in whatever case
  // their keyboard happens to be in, so fold those tokens to lowercase and let
  // the (case-insensitive) term matching handle the rest.
  normalizeQuery(term) {
    return term.replace(/\b(?:AND|OR|NOT)\b/g, (operator) => operator.toLowerCase());
  }

  fetchPrefixFallbackProducts(searchTerm) {
    return fetch(
      `${window.Shopify.routes.root}search?q=${encodeURIComponent(
        this.withWildcard(searchTerm)
      )}&section_id=predictive-search-fallback&type=product&options[unavailable_products]=show&options[prefix]=last`,
      { signal: this.abortController.signal }
    )
      .then((response) => (response.ok ? response.text() : null))
      .then((text) => {
        if (!text) return null;

        const fallbackDoc = new DOMParser().parseFromString(text, 'text/html');
        const group = fallbackDoc.querySelector('.predictive-search__result-group');
        if (!group) return null;

        const wrapper = document.createElement('div');
        wrapper.id = 'predictive-search-results-groups-wrapper';
        wrapper.className = 'predictive-search__results-groups-wrapper predictive-search__results-groups-wrapper--no-suggestions';
        wrapper.appendChild(group);

        const liveRegionText = fallbackDoc.querySelector('[data-predictive-search-live-region-count-value]')?.textContent;

        return { wrapper, liveRegionText };
      })
      .catch(() => null);
  }

  sliderCall(){
    // MatatCardImageSlider builds only the sliders whose card opted this width
    // in (settings.enable_image_slider_desktop / _mobile) and attaches
    // MatatCardSlider for them.
    MatatCardImageSlider.setupAll(document.querySelectorAll('predictive-search .flikity_grid_image'), {
      "imagesLoaded": true,
      "rightToLeft": true,
      "cellAlign": "right",
      "pageDots": true,
      "wrapAround": true,
      "draggable": false,
      "prevNextButtons": false,
      "responsive": [
        {
          "breakpoint": 750,
          "settings": {
            "freeScroll": true,
            "wrapAround": false,
            "contain": true,
            "prevNextButtons": false,
            "draggable": false,
            "pageDots": true
          }
        }
      ]
    });
  }

  setLiveRegionLoadingState() {
    this.statusElement = this.statusElement || this.closest(".details").querySelector('.predictive-search-status');
    this.loadingText = this.loadingText || this.closest(".details").getAttribute('data-loading-text');

    this.setLiveRegionText(this.loadingText);
    this.setAttribute('loading', true);
  }

  setLiveRegionText(statusText) {
    this.statusElement.setAttribute('aria-hidden', 'false');
    this.statusElement.textContent = statusText;

    setTimeout(() => {
      this.statusElement.setAttribute('aria-hidden', 'true');
    }, 1000);
  }

  renderSearchResults(resultsMarkup) {
    this.predictiveSearchResults.innerHTML = resultsMarkup;
    this.setAttribute('results', true);
    this.prioritizeExactProductMatches(this.searchTerm);
    this.sliderCall();
    this.setLiveRegionResults();
    this.open();
  }

  // Shopify's predictive search relevance can rank (or drop) short/common
  // query words (eg. "or") below literal substring matches. Re-sort the
  // already-returned products so a title that actually contains the typed
  // term is always shown first, without changing which products are fetched.
  prioritizeExactProductMatches(searchTerm) {
    const list = this.predictiveSearchResults.querySelector('#predictive-search-results-products-list');
    if (!list) return;

    const normalizedTerm = searchTerm.trim().toLowerCase().replace(/\s+/g, ' ');
    if (!normalizedTerm) return;

    const matches = [];
    const rest = [];

    Array.from(list.children).forEach((item) => {
      const heading = item.querySelector('.predictive-search__item-heading');
      const title = (heading?.textContent || '').trim().toLowerCase().replace(/\s+/g, ' ');
      (title.includes(normalizedTerm) ? matches : rest).push(item);
    });

    if (!matches.length || !rest.length) return;

    matches.concat(rest).forEach((item) => list.appendChild(item));
  }

  setLiveRegionResults() {
    this.removeAttribute('loading');
    this.setLiveRegionText(this.closest(".details").querySelector('[data-predictive-search-live-region-count-value]').textContent);
  }

  getResultsMaxHeight() {
    this.resultsMaxHeight = window.innerHeight - document.querySelector('.section-header').getBoundingClientRect().bottom;
    return this.resultsMaxHeight;
  }

  open() {
    this.predictiveSearchResults.style.maxHeight = this.resultsMaxHeight || `${this.getResultsMaxHeight()}px`;
    this.closest(".details").querySelector(".search-modal.modal__content").setAttribute('open', true);
    this.input.setAttribute('aria-expanded', true);
    this.isOpen = true;
  }

  close(clearSearchTerm = false) {
    this.closeResults(clearSearchTerm);
    this.isOpen = false;
  }

  closeResults(clearSearchTerm = false) {
    if (clearSearchTerm) {
      this.input.value = '';
      this.removeAttribute('results');
    }
    const selected = this.querySelector('[aria-selected="true"]');

    if (selected) selected.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', '');
    this.closest(".details").querySelector(".search-modal.modal__content").removeAttribute('loading');
    this.closest(".details").querySelector(".search-modal.modal__content").removeAttribute('open');
    this.input.setAttribute('aria-expanded', false);
    this.resultsMaxHeight = false
    this.predictiveSearchResults.removeAttribute('style');
  }
}

customElements.define('predictive-search', PredictiveSearch);
